import { observer } from 'mobx-react-lite'
import { useForm, Controller } from 'react-hook-form'
import { FormSelect, FormTextarea } from 'components/common/form'
import { yupResolver } from '@hookform/resolvers/yup'
import { contactReasonsOptions } from 'constants/contactReasons'
import { ContactSupportSchema } from 'schemas/ContactSupport'
import ErrorFieldForm from 'components/UI/ErrorFieldForm'
import { ErrorMessage } from '@hookform/error-message'
import Notice from '@ui-components-3/ui/lib/components/Notice'

const SCHEDULING_CHANGE = 'scheduling_change'

type SupportTicketFormProps = {
  createTicket: (type: string, details: string) => Promise<void>
}

const SupportTicketForm = ({ createTicket }: SupportTicketFormProps) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors, isDirty },
  } = useForm({
    resolver: yupResolver(ContactSupportSchema),
    mode: 'onSubmit',
  })
  const selectedReason = watch('type')

  const submitHandler = async (values) => {
    await createTicket?.(values.type, values.details)
  }

  return (
    <div className="px-[60px] pt-4 pb-8 flex flex-col">
      <h6 className="mb-4 typography-body-xl text-primary-800 font-medium">Need Support?</h6>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className="mb-6">
          <Controller
            name={'type'}
            control={control}
            render={({ field }) => (
              <FormSelect
                {...field}
                label={'Please help me with'}
                options={contactReasonsOptions}
                getOptionLabel={(s) => s.label}
                getOptionValue={(s) => s.value}
                onChange={(e) => field.onChange(e.target.value)}
                data-testid="support-ticket-select"
              />
            )}
          />
          <ErrorMessage errors={errors} name={'type'} as={<ErrorFieldForm />} />
          {selectedReason === SCHEDULING_CHANGE && (
            <Notice variant="info" className="mt-8">
              <p className="typography-body-m text-neutral-800">
                Please include the timezone in your scheduling change request.
              </p>
            </Notice>
          )}
        </div>
        <div className="mb-6">
          <FormTextarea rows={7} autoComplete={'off'} label={'Description'} {...register('details')} />
          <ErrorMessage errors={errors} name={'details'} as={<ErrorFieldForm />} />
        </div>
        <button type={'submit'} disabled={isSubmitting || !isDirty} className="btn btn-primary float-right">
          Send Message
        </button>
      </form>
    </div>
  )
}

export default observer(SupportTicketForm)
